import { FusionDropDownInput } from 'components/index';
import { usePerms } from 'contexts/PermsContext';
import { selectVisibleDeviceTypes } from 'features/integrations/state/selectors/integrationSelectors';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/integrations/utils/deviceComparisionUtils';
import { canViewDeviceType } from 'features/integrations/utils/integrationVisibilityUtils';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import DeviceMap from '../features/DeviceMap/index.web';

const FloodMapWidget = () => {
  const perms = usePerms();
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const floodDevices = useMemo(() => {
    if (!connectedDevices?.length) return [];
    return connectedDevices
      .map((device) => ({ device }))
      .filter(
        ({ device }) =>
          (DeviceIsSystemType(device, 'Flood') && DeviceIsDeviceType(device, 'Stage')) ||
          (DeviceIsSystemType(device, 'USGS') &&
            DeviceIsDeviceType(device, 'MonitoringStation') &&
            canViewDeviceType(device, perms, visibleDeviceTypes)),
      )
      .sort((a, b) => parseInt(a.device.node_id || 0, 10) - parseInt(b.device.node_id || 0, 10));
  }, [connectedDevices, perms, visibleDeviceTypes]);

  const deviceOptions = useMemo(
    () =>
      floodDevices.map(
        ({ device }) => `${device.static_id ? `${device.static_id} ` : ''}${device.alias}`,
      ),
    [floodDevices],
  );

  useEffect(() => {
    if (floodDevices.length === 0) {
      setSelectedDevice(null);
      setSelectedIndex(-1);
      return;
    }

    if (!selectedDevice) {
      setSelectedDevice(floodDevices[0].device);
      setSelectedIndex(0);
      return;
    }

    const nextIndex = floodDevices.findIndex(({ device }) => device.id === selectedDevice.id);
    if (nextIndex === -1) {
      setSelectedDevice(floodDevices[0].device);
      setSelectedIndex(0);
      return;
    }

    if (nextIndex !== selectedIndex) {
      setSelectedIndex(nextIndex);
    }
  }, [floodDevices, selectedDevice, selectedIndex]);

  const handleDeviceChange = (value) => {
    const index = Number(value);
    if (!Number.isFinite(index) || index < 0) return;
    const selected = floodDevices[index];
    if (!selected) return;
    setSelectedIndex(index);
    setSelectedDevice(selected.device);
  };

  return (
    <div className="d-flex flex-column h-100 w-100 min-h-0 overflow-hidden">
      <div className="flex-grow-1 min-h-0 overflow-hidden rounded-3">
        <DeviceMap
          deviceTypeToFilter="stage"
          enableFilters={false}
          selectedDeviceOverride={selectedDevice}
          setUseThisDevice={setSelectedDevice}
        />
      </div>
      <div className="mt-2">
        <FusionDropDownInput
          options={deviceOptions}
          value={selectedIndex}
          onChange={handleDeviceChange}
          disabled={deviceOptions.length === 0}
        />
      </div>
    </div>
  );
};

export default FloodMapWidget;
